import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import speakingData from '../data/speakingTest.json';
import { FaMicrophone, FaStop, FaPlay, FaCheck, FaVolumeUp, FaHeadphones, FaVolumeMute, FaCheckCircle, FaGraduationCap, FaArrowRight } from 'react-icons/fa';
import '../App.css';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { trackGA4Event } from '../utils/GA4Analytics';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import EbookRecommendSlide from '../components/EbookRecommendSlide';
// --- Helper Functions for Scoring ---
const calculateSimilarity = (s1, s2) => {
    if (!s1 || !s2) return 0;
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    if (longerLength === 0) return 1.0;
    return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
};

const editDistance = (s1, s2) => {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
        let lastValue = i;
        for (let j = 0; j <= s2.length; j++) {
            if (i === 0) costs[j] = j;
            else if (j > 0) {
                let newValue = costs[j - 1];
                if (s1.charAt(i - 1) !== s2.charAt(j - 1))
                    newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                costs[j - 1] = lastValue;
                lastValue = newValue;
            }
        }
        if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
};

const SpeakingTest = () => {
    const { mode } = useParams(); // 'full' or specific section (e.g., 'readAloud')
    const navigate = useNavigate();
    const location = useLocation();
    const testId = (location.state?.testId || 1).toString();

    // --- State ---
    const [sectionName, setSectionName] = useState('');
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedPattern, setSelectedPattern] = useState('bpo'); // Default to 'bpo' for direct Speaking test start

    useEffect(() => {
        document.body.classList.add('notranslate');
        return () => {
            document.body.classList.remove('notranslate');
        };
    }, []);

    const [showPreTestInstruction, setShowPreTestInstruction] = useState(true); // Full test env check
    const [isInstruction, setIsInstruction] = useState(true); // Section instruction
    const [isRecording, setIsRecording] = useState(false);
    const [isListening, setIsListening] = useState(false); // True when TTS is playing
    const [transcript, setTranscript] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);

    const [showEbookRecommend, setShowEbookRecommend] = useState(false);
    const [checkingEligibility, setCheckingEligibility] = useState(true);
    
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [hasAccess, setHasAccess] = useState(true);

    const recognitionRef = useRef(null);
    const transcriptRef = useRef('');
    const timersRef = useRef([]); // Track active timers
    const testResponsesRef = useRef([]); // Track all responses for the result page

    // --- Microphone Hardware Pre-Check Hooks ---
    const [preCheckStep, setPreCheckStep] = useState(1); // 1: Mic Access, 2: Record 3s, 3: Playback Verification
    const [micStream, setMicStream] = useState(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [testAudioBlob, setTestAudioBlob] = useState(null);
    const [isRecordingTest, setIsRecordingTest] = useState(false);
    const [testRecordTime, setTestRecordTime] = useState(3);
    const [isPlayingTest, setIsPlayingTest] = useState(false);
    const [micVolume, setMicVolume] = useState(0);
    const [micError, setMicError] = useState('');
    
    const preCheckAudioContextRef = useRef(null);
    const preCheckAnalyserRef = useRef(null);
    const preCheckAnimationFrameRef = useRef(null);
    const testChunksRef = useRef([]);
    const preCheckAudioRef = useRef(null);

    const startVolumeMonitor = async (stream) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContextClass();
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            
            preCheckAudioContextRef.current = audioContext;
            preCheckAnalyserRef.current = analyser;
            
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            
            const updateVolume = () => {
                if (!preCheckAnalyserRef.current) return;
                preCheckAnalyserRef.current.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const average = sum / bufferLength;
                setMicVolume(Math.min(100, Math.round((average / 128) * 100)));
                preCheckAnimationFrameRef.current = requestAnimationFrame(updateVolume);
            };
            
            updateVolume();
        } catch (e) {
            console.warn("Could not start audio volume visualizer:", e);
        }
    };

    const stopVolumeMonitor = () => {
        if (preCheckAnimationFrameRef.current) {
            cancelAnimationFrame(preCheckAnimationFrameRef.current);
            preCheckAnimationFrameRef.current = null;
        }
        if (preCheckAudioContextRef.current && preCheckAudioContextRef.current.state !== 'closed') {
            preCheckAudioContextRef.current.close();
        }
        preCheckAnalyserRef.current = null;
        preCheckAudioContextRef.current = null;
    };

    const requestMicPermission = async () => {
        setMicError('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicStream(stream);
            startVolumeMonitor(stream);
            setPreCheckStep(2);
        } catch (e) {
            console.error("Microphone permission denied:", e);
            setMicError("Microphone access blocked. Please click the camera/microphone icon in Chrome's address bar, set it to 'Allow', and click 'Retry Permission'.");
        }
    };

    const startTestRecording = () => {
        if (!micStream) return;
        setIsRecordingTest(true);
        setTestRecordTime(3);
        testChunksRef.current = [];

        const recorder = new MediaRecorder(micStream);
        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
                testChunksRef.current.push(e.data);
            }
        };

        recorder.onstop = () => {
            const mimeType = recorder.mimeType || 'audio/webm';
            const blob = new Blob(testChunksRef.current, { type: mimeType });
            setTestAudioBlob(blob);
            setIsRecordingTest(false);
            setPreCheckStep(3);
        };

        recorder.start();
        setMediaRecorder(recorder);

        let count = 3;
        const interval = setInterval(() => {
            count--;
            setTestRecordTime(count);
            if (count === 0) {
                clearInterval(interval);
                recorder.stop();
            }
        }, 1000);
    };

    const playTestRecording = () => {
        if (!testAudioBlob || !preCheckAudioRef.current) return;
        const url = URL.createObjectURL(testAudioBlob);
        preCheckAudioRef.current.src = url;
        preCheckAudioRef.current.load();
        
        const playPromise = preCheckAudioRef.current.play();
        if (playPromise !== undefined) {
            playPromise.then(() => {
                setIsPlayingTest(true);
                preCheckAudioRef.current.onended = () => {
                    setIsPlayingTest(false);
                };
            }).catch(error => {
                console.error("Safari audio playback blocked:", error);
                setIsPlayingTest(false);
            });
        }
    };

    useEffect(() => {
        return () => {
            stopVolumeMonitor();
            if (micStream) {
                micStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [micStream]);

    // --- Access Control Check ---
    useEffect(() => {
        const checkAccess = async () => {
            // Test 1 is always free
            if (testId === "1") {
                setCheckingAccess(false);
                setHasAccess(true);
                return;
            }

            const authInstance = getAuth();
            const unsubscribe = authInstance.onAuthStateChanged(async (currentUser) => {
                if (!currentUser) {
                    setHasAccess(false);
                    navigate('/login');
                    setCheckingAccess(false);
                    return;
                }

                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const rawPremium = userData.hasPaid === true || userData.paidTests === true || userData.isPremium === true || userData.hasPremiumAccess === true;
                        let isExpired = false;
                        if (userData.premiumExpiresAt) {
                            try {
                                const expiryDate = userData.premiumExpiresAt.toDate ? userData.premiumExpiresAt.toDate() : new Date(userData.premiumExpiresAt);
                                if (new Date() > expiryDate) {
                                    isExpired = true;
                                }
                            } catch (err) {
                                console.error("Error parsing expiration date:", err);
                            }
                        }
                        const isFullPremium = rawPremium && !isExpired;
                        
                        if (mode === 'full') {
                            if (isFullPremium) {
                                setHasAccess(true);
                            } else {
                                setHasAccess(false);
                                navigate('/pricing');
                            }
                        } else {
                            // Section practice
                            const isSectionUnlocked = userData.unlockedSections?.[mode] === true;
                            if (isFullPremium || isSectionUnlocked) {
                                setHasAccess(true);
                            } else {
                                setHasAccess(false);
                                navigate('/pricing');
                            }
                        }
                    } else {
                        setHasAccess(false);
                        navigate('/pricing');
                    }
                } catch (err) {
                    console.error("Error checking access in SpeakingTest:", err);
                    setHasAccess(false);
                    navigate('/pricing');
                }
                setCheckingAccess(false);
            });

            return () => unsubscribe();
        };

        checkAccess();
    }, [mode, testId, navigate]);

    // --- eBook Recommendation Eligibility Check ---
    useEffect(() => {
        const checkEligibility = async () => {
            // 1. Check local storage purchase flag
            if (localStorage.getItem('ebook_purchased') === 'true') {
                setCheckingEligibility(false);
                return;
            }

            // 2. Check dismissed today
            const todayStr = new Date().toDateString();
            if (localStorage.getItem('ebook_recommend_dismissed_date') === todayStr) {
                setCheckingEligibility(false);
                return;
            }

            // 3. Check Firestore for registered user
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        if (userData.hasPurchasedEbook || userData.hasPaid || userData.paidTests) {
                            localStorage.setItem('ebook_purchased', 'true');
                            setCheckingEligibility(false);
                            return;
                        }
                    }
                } catch (err) {
                    console.error("Error checking user purchase status:", err);
                }
            }

            // Show recommendation slide if eligible
            setShowEbookRecommend(true);
            setCheckingEligibility(false);
        };

        const auth = getAuth();
        const unsubscribe = auth.onAuthStateChanged(() => {
            checkEligibility();
        });
        return () => unsubscribe();
    }, []);

    // --- Navigation Warning ---
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (questions.length > 0 && !showResult) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [questions, showResult]);

    // Helper to clear all timers
    const clearAllTimers = () => {
        timersRef.current.forEach(id => clearTimeout(id));
        timersRef.current = [];
    };

    // Full Test Order
    const bpoSectionOrder = [
        'readAloud',
        'repeats',
        'shortAnswer',
        'sentenceBuilds',
        'storyRetelling',
        'openQuestions'
    ];

    const veptSectionOrder = [
        'readAloud',
        'repeats',
        'sentenceBuilds',
        'openQuestions'
    ];

    const sectionOrder = selectedPattern === 'vept' ? veptSectionOrder : bpoSectionOrder;

    // --- Initialization ---
    useEffect(() => {
        trackGA4Event('instruction_page_view', { test_type: 'speaking', test_id: testId || 1 });
    }, [testId]);

    useEffect(() => {
        if (!selectedPattern && mode === 'full') return; // Wait until pattern is selected

        let targetSection = mode;
        if (mode === 'full') {
            targetSection = sectionOrder[0];
        }

        clearAllTimers(); // Reset timers on section change

        // Load Data based on the active test ID (Default to 1)
        const currentTestData = speakingData[testId] || speakingData["1"] || {};
        const sectionData = currentTestData[targetSection] || [];
        setSectionName(targetSection);
        setQuestions(sectionData);
        setCurrentQuestionIndex(0);

        // Only reset score and responses if mode changes conceptually 
        // For full mode, keep appending.
        if (targetSection === sectionOrder[0]) {
            setScore(0);
            testResponsesRef.current = [];
        }

        setShowResult(false);
        setFeedbackMessage('');
        setTranscript('');
        transcriptRef.current = '';
        setIsInstruction(true); // Always start new section with instruction

    }, [mode, selectedPattern]);

    // Section Started Analytics Tracking
    useEffect(() => {
        if (!showPreTestInstruction && !isInstruction && sectionName) {
            let trackerName = sectionName;
            if (sectionName === 'readAloud') trackerName = 'reading';
            else if (sectionName === 'repeats') trackerName = 'repeat_sentence';
            else if (sectionName === 'shortAnswer') trackerName = 'listening';
            else if (sectionName === 'sentenceBuilds') trackerName = 'speaking';
            else if (sectionName === 'storyRetelling') trackerName = 'story_retelling';
            else if (sectionName === 'openQuestions') trackerName = 'open_questions';

            trackFunnelEvent(`${trackerName}_started`);
        }
    }, [isInstruction, sectionName, showPreTestInstruction]);

    // --- Speech Recognition Setup ---
    useEffect(() => {
        // Check browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let finalTrans = '';
                for (let i = 0; i < event.results.length; ++i) {
                    finalTrans += event.results[i][0].transcript + ' ';
                }
                const cleanedTrans = finalTrans.trim();
                setTranscript(cleanedTrans);
                transcriptRef.current = cleanedTrans; // Sync ref
            };

            recognition.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsRecording(false);
                setFeedbackMessage("Error: " + event.error);
            };

            recognitionRef.current = recognition;
        } else {
            setFeedbackMessage("⚠️ Your browser does not support Speech Recognition. Please use Chrome.");
        }
    }, []);

    // --- Audio Visualization & Voice Logic ---
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const streamRef = useRef(null);

    // Stop Audio Stream
    const stopAudioStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    };

    // Start Visualizer
    const startVisualizer = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;

            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);

            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const canvas = canvasRef.current;
            if (!canvas) return;
            const canvasCtx = canvas.getContext("2d");

            const draw = () => {
                if (!isRecording) return; // Stop drawing if not recording
                requestAnimationFrame(draw);

                analyser.getByteFrequencyData(dataArray);

                canvasCtx.fillStyle = 'rgb(249, 250, 251)'; // Match bg-gray-50
                canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

                const barWidth = (canvas.width / bufferLength) * 2.5;
                let barHeight;
                let x = 0;

                for (let i = 0; i < bufferLength; i++) {
                    barHeight = dataArray[i] / 2;

                    // Gradient Wave
                    const r = barHeight + 25 * (i / bufferLength);
                    const g = 250 * (i / bufferLength);
                    const b = 50;

                    canvasCtx.fillStyle = `rgb(${r},${g},${b})`;
                    canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

                    x += barWidth + 1;
                }
            };

            draw();

        } catch (err) {
            console.error("Error accessing microphone for visualizer:", err);
        }
    };

    // Cleanup Audio on Unmount or Stop
    useEffect(() => {
        if (!isRecording) {
            stopAudioStream();
        } else {
            startVisualizer();
        }
        return () => stopAudioStream();
    }, [isRecording]);


    // Global array to prevent garbage collection bugs in Web Speech API (Chrome/Safari)
    window.utterances = window.utterances || [];

    // --- Stable TTS with Chunking ---
    const activeAudioRef = useRef(null);

    const stopAllSpeech = () => {
        if (activeAudioRef.current) {
            activeAudioRef.current.pause();
            activeAudioRef.current.onended = null;
            activeAudioRef.current.onerror = null;
            activeAudioRef.current = null;
        }
        if (window.utterances) {
            window.utterances.forEach(utter => {
                utter.onend = null;
                utter.onerror = null;
            });
            window.utterances = [];
        }
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    };

    const speakText = (text, onEnd) => {
        if (!text) { if (onEnd) onEnd(); return; }

        stopAllSpeech();
        window.utterances = []; // Clear previous refs

        // 1. Chunking text to prevent SpeechSynthesis from cutting off/glitching out on long strings
        const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
        let currentChunk = 0;

        const speakNextChunk = () => {
             if (currentChunk >= sentences.length) {
                 if (onEnd) onEnd();
                 return;
             }
             
             const chunkText = sentences[currentChunk].trim();
             if (!chunkText) {
                 currentChunk++;
                 speakNextChunk();
                 return;
             }

             const utter = new SpeechSynthesisUtterance(chunkText);
             window.utterances.push(utter); // Maintain GC ref
             currentChunk++;

             const voices = window.speechSynthesis.getVoices();
             let preferredVoice = 
                 voices.find(v => v.name === 'Samantha') ||
                 voices.find(v => v.name === 'Karen') ||
                 voices.find(v => v.name === 'Daniel') ||
                 voices.find(v => v.name.includes('Microsoft Zira')) || // Windows Stable
                 voices.find(v => v.name === 'Google US English') ||
                 voices.find(v => v.lang === 'en-US' && v.localService) ||
                 voices.find(v => v.lang === 'en-US') ||
                 (voices.length > 0 ? voices[0] : null);

             if (preferredVoice) {
                 utter.voice = preferredVoice;
             }
             
             utter.lang = 'en-US';
             utter.rate = 0.88; // Natural, relaxed human cadence
             utter.pitch = 1.0;

             utter.onend = () => {
                 setTimeout(() => {
                     speakNextChunk(); // Chain the next sentence with natural pause
                     window.utterances = window.utterances.filter(u => u !== utter);
                 }, 250);
             };

             utter.onerror = (e) => {
                 console.error("SpeechSynthesis Error:", e);
                 speakNextChunk(); // Continue even if one chunk fails
                 window.utterances = window.utterances.filter(u => u !== utter);
             };

             window.speechSynthesis.speak(utter);
        };

        // Wait for voices if not loaded (fixes initial robotic voice issue)
        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = () => {
                window.speechSynthesis.onvoiceschanged = null; // unset
                speakNextChunk();
            };
            // Fallback timeout
            setTimeout(() => {
                if (currentChunk === 0) speakNextChunk();
            }, 500);
        } else {
            speakNextChunk();
        }
    };

    const playAudioOrSpeak = (audioUrl, fallbackText, onEnd) => {
        stopAllSpeech();
        if (!audioUrl) {
            if (onEnd) onEnd();
            return;
        }

        const audio = new Audio(audioUrl);
        activeAudioRef.current = audio;

        audio.onended = () => {
            activeAudioRef.current = null;
            if (onEnd) onEnd();
        };

        audio.onerror = (e) => {
            console.warn(`[Audio Player] MP3 failed at ${audioUrl}:`, e);
            activeAudioRef.current = null;
            setTimeout(() => { if (onEnd) onEnd(); }, 500);
        };

        audio.play().catch(e => {
            console.warn(`[Audio Player] Autoplay prevented for ${audioUrl}:`, e);
            // Try user-triggered playback or proceed cleanly
            activeAudioRef.current = null;
            setTimeout(() => { if (onEnd) onEnd(); }, 500);
        });
    };



    // --- Auto-Flow Logic ---
    useEffect(() => {
        // Prevent background timers/audio if the initial instructions are showing
        if (showPreTestInstruction) {
            return;
        }

        // 0. Handle Instruction Phase
        if (isInstruction) {
            const instructionText = speakingData.instructions ? speakingData.instructions[sectionName] : "Please follow the instructions.";
            let instAudioUrl = '';
            if (sectionName === 'readAloud') instAudioUrl = '/audio/instructions/inst_part_a_reading.mp3';
            else if (sectionName === 'repeats') instAudioUrl = '/audio/instructions/inst_part_b_repeat.mp3';
            else if (sectionName === 'shortAnswer') instAudioUrl = '/audio/instructions/inst_part_c_questions.mp3';
            else if (sectionName === 'sentenceBuilds') instAudioUrl = '/audio/instructions/inst_part_d_builds.mp3';
            else if (sectionName === 'storyRetelling') instAudioUrl = '/audio/instructions/inst_part_e_story.mp3';
            else if (sectionName === 'openQuestions') instAudioUrl = '/audio/instructions/inst_part_f_open.mp3';

            setIsListening(true);

            const instTimer = setTimeout(() => {
                playAudioOrSpeak(instAudioUrl, instructionText, () => {
                    const waitTimer = setTimeout(() => {
                        setIsInstruction(false);
                        setIsListening(false);
                    }, 1500); // 1.5s delay
                    timersRef.current.push(waitTimer);
                });
            }, 600);
            timersRef.current.push(instTimer);

            return () => {
                clearAllTimers();
                stopAllSpeech();
            };
        }

        if (!questions.length) return;
        const q = questions[currentQuestionIndex];
        if (!q) return;

        // 1. Reset State
        setTranscript('');
        transcriptRef.current = '';
        setFeedbackMessage('');
        setIsRecording(false);
        setIsListening(false);
        stopAudioStream();

        if (recognitionRef.current) recognitionRef.current.abort();

        // --- TIMING CONFIGURATION ---
        let prepTime = 0;
        let recordingTime = 10000;
        switch (sectionName) {
            case 'readAloud': prepTime = 500; recordingTime = 8000; break;
            case 'repeats': prepTime = 500; recordingTime = 8000; break;
            case 'shortAnswer': prepTime = 500; recordingTime = 6000; break;
            case 'sentenceBuilds': prepTime = 500; recordingTime = 10000; break;
            case 'storyRetelling': prepTime = 1000; recordingTime = 30000; break;
            case 'openQuestions': prepTime = 2000; recordingTime = 40000; break;
            default: prepTime = 500; recordingTime = 10000;
        }

        // Helper to start recording flow
        const startRecordingSequence = () => {
            setIsListening(false);

            // Start prep timer
            const prepTimer = setTimeout(() => {
                handleStartRecording();

                // Stop after Recording Time
                const recTimer = setTimeout(() => {
                    handleStopRecording();
                    // Move Next
                    const nextTimer = setTimeout(() => {
                        handleNext();
                    }, 1000);
                    timersRef.current.push(nextTimer);
                }, recordingTime);
                timersRef.current.push(recTimer);
            }, prepTime);
            timersRef.current.push(prepTimer);
        };

        // 2. Play Audio or Start Direct Flow
        let textToPlay = '';
        let questionAudioUrl = '';
        const setStr = String(testId).padStart(2, '0');
        const qIdStr = String(q.id || (currentQuestionIndex + 1)).padStart(2, '0');

        if (sectionName === 'repeats') {
            textToPlay = q.text || q.question || '';
            questionAudioUrl = `/audio/speaking/set_${setStr}/speaking_set${setStr}_repeats_q${qIdStr}.mp3`;
        } else if (sectionName === 'shortAnswer') {
            textToPlay = q.question || q.text || '';
            questionAudioUrl = `/audio/speaking/set_${setStr}/speaking_set${setStr}_questions_q${qIdStr}.mp3`;
        } else if (sectionName === 'sentenceBuilds') {
            textToPlay = Array.isArray(q.parts) ? q.parts.join(' ... ') : (q.text || q.parts || '');
            questionAudioUrl = `/audio/speaking/set_${setStr}/speaking_set${setStr}_builds_q${qIdStr}.mp3`;
        } else if (sectionName === 'storyRetelling') {
            textToPlay = q.passage || q.text || '';
            questionAudioUrl = `/audio/speaking/set_${setStr}/speaking_set${setStr}_story_q${qIdStr}.mp3`;
        } else if (sectionName === 'openQuestions') {
            textToPlay = q.prompt || q.text || q.question || '';
            questionAudioUrl = `/audio/speaking/set_${setStr}/speaking_set${setStr}_open_q${qIdStr}.mp3`;
        }

        if (textToPlay || questionAudioUrl) {
            setIsListening(true);
            const flowTimer = setTimeout(() => {
                playAudioOrSpeak(questionAudioUrl, textToPlay, () => {
                    startRecordingSequence();
                });
            }, 600);
            timersRef.current.push(flowTimer);
        } else {
            // Read Aloud
            startRecordingSequence();
        }

        return () => {
            clearAllTimers();
            stopAllSpeech();
            stopAudioStream();
        };

    }, [currentQuestionIndex, sectionName, questions, isInstruction, showPreTestInstruction]);

    // --- Handlers ---
    const handleStartRecording = () => {
        if (!recognitionRef.current) return;
        // 1. Reset State
        setTranscript('');
        transcriptRef.current = ''; // Reset Ref
        setFeedbackMessage('');
        setIsRecording(false);
        setIsListening(false);
        try {
            setIsRecording(true);
            recognitionRef.current.start();
        } catch (e) {
            console.error(e);
        }
    };

    const handleStopRecording = () => {
        if (!recognitionRef.current) return;
        setIsRecording(false);
        try { recognitionRef.current.stop(); } catch (e) { }

        // Save Response Object
        const textToSave = transcriptRef.current || '';
        const currentQ = questions[currentQuestionIndex];

        if (currentQ) {
            // Prevent duplicate saves for the same question
            const existingIdx = testResponsesRef.current.findIndex(r => r.section === sectionName && r.questionId === currentQ.id);
            if (existingIdx !== -1) return;

            // Calculate Score immediately
            const points = calculateScoreForQuestion();
            setScore(prev => prev + points);

            testResponsesRef.current.push({
                testId: testId,
                section: sectionName,
                questionId: currentQ.id,
                questionText: currentQ.text || currentQ.question || currentQ.audioText || (currentQ.parts ? currentQ.parts.join('. ') : ''),
                transcript: textToSave,
                score: points
            });
        }
    };



    const calculateScoreForQuestion = () => {
        // Use Ref to get latest text inside the timeout callback
        const textToScore = transcriptRef.current;
        if (!textToScore) return 0;

        const q = questions[currentQuestionIndex];
        if (!q) return 0;

        const userText = textToScore.trim().toLowerCase();
        let points = 0;

        // Fluency Bonus (Speed)
        const wordCount = userText.split(' ').length;
        // Placeholder for fluency logic
        // if (wordCount > 0) {
        //     const expectedDuration = 10; // seconds
        //     const actualDuration = 10; // Need to track recording duration
        //     const wpm = wordCount / (actualDuration / 60);
        //     if (wpm > 120) points += 2; // Example bonus
        // }

        // 1. Read Aloud / Repeats / Sentence Builds (Exact Match)
        if (['readAloud', 'repeats', 'sentenceBuilds'].includes(sectionName)) {
            const target = (q.text || q.answer || '').toLowerCase();
            const sim = calculateSimilarity(userText, target);
            if (sim > 0.85) points = 10;
            else if (sim > 0.6) points = 7;
            else if (sim > 0.4) points = 4;
            else points = 1;
        }
        // 2. Short Answer (Keyword Match)
        else if (sectionName === 'shortAnswer') {
            if (q.answer && q.answer.some(ans => userText.includes(ans.toLowerCase()))) {
                points = 10;
            }
        }
        // 3. Open Ended (Story / Open)
        else {
            // Mock scoring based on length for now
            if (userText.split(' ').length > 15) points = 8;
            else if (userText.length > 5) points = 4;
            // Ideally: send to Gemini API here
        }
        return points;
    };

    const handleNext = () => {
        // Ensure we save whatever was recorded if they click next early before the timer ends
        handleStopRecording();

        // Navigation Logic
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            // End of this section
            let trackerName = sectionName;
            if (sectionName === 'readAloud') trackerName = 'reading';
            else if (sectionName === 'repeats') trackerName = 'repeat_sentence';
            else if (sectionName === 'shortAnswer') trackerName = 'listening';
            else if (sectionName === 'sentenceBuilds') trackerName = 'speaking';
            else if (sectionName === 'storyRetelling') trackerName = 'story_retelling';
            else if (sectionName === 'openQuestions') trackerName = 'open_questions';

            trackFunnelEvent(`${trackerName}_completed`);

            if (mode === 'full') {
                const currentIdx = sectionOrder.indexOf(sectionName);
                if (currentIdx < sectionOrder.length - 1) {
                    const nextSec = sectionOrder[currentIdx + 1];
                    const currentTestData = speakingData[testId] || speakingData["1"] || {};
                    setSectionName(nextSec);
                    setQuestions(currentTestData[nextSec] || []);
                    setCurrentQuestionIndex(0);
                    setIsInstruction(true); // Trigger instruction for new section
                } else {
                    trackGA4Event('test_completed', { test_type: 'speaking', test_id: testId || 1 });
                    if (window.gtag) window.gtag('event', 'test_completed');
                    trackFunnelEvent('test_completed');
                    navigate('/result', {
                        state: {
                            mode: 'speaking',
                            testResponses: testResponsesRef.current,
                            totalAppScore: score,
                            testId: 1 // Default to 1, could be dynamic later
                        }
                    });
                }
            } else {
                trackGA4Event('test_completed', { test_type: 'speaking', test_id: testId || 1 });
                if (window.gtag) window.gtag('event', 'test_completed');
                trackFunnelEvent('test_completed');
                navigate('/result', {
                    state: {
                        mode: 'speaking',
                        isPractice: true,
                        section: sectionName,
                        testResponses: testResponsesRef.current,
                        totalAppScore: score,
                        testId: 1
                    }
                });
            }
        }
    };

    // --- Render ---

    if (checkingAccess) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    if (checkingEligibility) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    if (showEbookRecommend) {
        return (
            <EbookRecommendSlide 
                testType="speaking" 
                onContinue={() => setShowEbookRecommend(false)} 
            />
        );
    }

    if (showResult) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FaCheck className="text-4xl text-green-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">Test Complete!</h2>
                    <p className="text-gray-500 mb-6">Great job finishing the {mode === 'full' ? 'full speaking assessment' : sectionName} module.</p>

                    <div className="bg-indigo-50 rounded-xl p-4 mb-8">
                        <span className="block text-sm text-indigo-600 font-bold uppercase tracking-wider">Estimated Score</span>
                        <span className="block text-5xl font-extrabold text-indigo-700 mt-2">{score}</span>
                    </div>

                    <button
                        onClick={() => navigate('/versant-speaking-and-listening-practice-test')}
                        className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all"
                    >
                        Back to Speaking Hub
                    </button>
                </div>
            </div>
        );
    }

    // Pre-Test Environment Check View (Speaking test directly starts with Audio & Mic verification)
    if (showPreTestInstruction) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 sm:p-6 font-sans">
                <audio ref={preCheckAudioRef} className="hidden" playsInline />
                <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col md:flex-row">

                    {/* Header Side (Left on desktop, Top on mobile) */}
                    <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 p-8 md:p-12 text-white text-center flex flex-col justify-center relative md:w-2/5">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-md shadow-inner border border-white/20">
                            <FaHeadphones className="text-3xl sm:text-4xl text-indigo-100 animate-bounce" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">Audio Check</h2>
                        <p className="text-indigo-200 text-sm sm:text-base font-medium leading-relaxed">
                            Verify your microphone and hardware setup to ensure accurate scoring for Test {testId}.
                        </p>
                        
                        {/* Progress Steps Indicators */}
                        <div className="mt-8 flex items-center justify-center gap-3 relative z-10">
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 1 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 2 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 3 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                        </div>
                    </div>

                    {/* Body Side (Right on desktop, Bottom on mobile) */}
                    <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-between md:w-3/5 min-h-[450px]">
                        
                        {/* Step 1: Request Mic Access */}
                        {preCheckStep === 1 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">1</span>
                                    Microphone Access
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    This test grades your speaking flow. We need permission to access your microphone. Click the button below to request access.
                                </p>
                                
                                {micError ? (
                                    <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm text-red-700 mb-6 leading-relaxed animate-shake flex gap-3 items-start">
                                        <span className="text-lg mt-0.5">⚠️</span>
                                        <div>
                                            <strong className="block font-bold mb-1">Access Blocked</strong>
                                            {micError}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-indigo-50/50 border border-indigo-100 p-5 rounded-2xl flex items-center gap-4 mb-6">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 shadow-sm animate-pulse">
                                            <FaMicrophone />
                                        </div>
                                        <p className="text-xs text-indigo-900 font-semibold leading-relaxed">
                                            Chrome will ask for permission when you click. Tap "Allow" to proceed.
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={requestMicPermission}
                                    className="w-full py-4 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 hover:-translate-y-0.5 transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    Enable Microphone
                                </button>
                            </div>
                        )}

                        {/* Step 2: Record Audio Test */}
                        {preCheckStep === 2 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">2</span>
                                    Voice Calibration
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    Speak into your microphone. Say: <strong className="text-indigo-600">"Testing microphone"</strong>. We will record a short 3-second sample to verify it works.
                                </p>

                                {/* Volume Bar Indicator */}
                                <div className="mb-6 bg-gray-50 border border-gray-100 p-5 rounded-2xl">
                                    <span className="text-xs uppercase tracking-wider font-extrabold text-gray-400 block mb-2">Live Microphone Signal</span>
                                    <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden relative shadow-inner">
                                        <div className="h-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700 transition-all duration-75" style={{ width: `${micVolume}%` }}></div>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-bold block mt-1.5">{micVolume > 10 ? '✨ Signal detected. Speak now.' : '🎤 Start speaking to test level.'}</span>
                                </div>

                                {isRecordingTest ? (
                                    <div className="w-full py-5 rounded-2xl border-2 border-indigo-600 bg-indigo-50/50 flex flex-col items-center justify-center text-center animate-pulse">
                                        <div className="w-4 h-4 rounded-full bg-red-600 animate-ping mb-3"></div>
                                        <span className="font-extrabold text-indigo-900 text-lg mb-1">RECORDING VOICE CHECK</span>
                                        <span className="text-sm text-indigo-600 font-medium">Stopping automatically in {testRecordTime} seconds...</span>
                                    </div>
                                ) : (
                                    <button
                                        onClick={startTestRecording}
                                        className="w-full py-4 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 hover:-translate-y-0.5 transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        Start 3s Voice Check
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Step 3: Playback Confirm */}
                        {preCheckStep === 3 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">3</span>
                                    Audio Verification
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    Listen to your voice recording to make sure your headset is selected and sounds clean.
                                </p>

                                <button
                                    onClick={playTestRecording}
                                    className={`w-full py-4 rounded-2xl font-black border-2 transition-all flex items-center justify-center gap-3 mb-6 cursor-pointer ${isPlayingTest ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50' : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50'}`}
                                >
                                    {isPlayingTest ? (
                                        <>
                                            <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                            Playing Back Your Voice...
                                        </>
                                    ) : (
                                        <>
                                            <FaPlay className="text-xs" />
                                            Play Back Sample Recording
                                        </>
                                    )}
                                </button>

                                <div className="border-t border-gray-100 pt-6">
                                    <p className="text-sm text-gray-700 font-bold mb-4 text-center">Can you hear your voice sample clearly?</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => {
                                                setPreCheckStep(2);
                                                setTestAudioBlob(null);
                                            }}
                                            className="py-3 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-gray-600 text-xs transition-colors cursor-pointer"
                                        >
                                            No, Record Again 🔄
                                        </button>
                                        <button
                                            onClick={() => {
                                                trackGA4Event('test_started', { test_type: 'speaking', test_id: testId || 1 });
                                                if (window.gtag) window.gtag('event', 'test_start');
                                                trackFunnelEvent('test_started', { test_type: 'speaking', test_id: testId || 1 });
                                                
                                                stopVolumeMonitor();
                                                if (micStream) {
                                                    micStream.getTracks().forEach(track => track.stop());
                                                }
                                                stopAllSpeech();
                                                setShowPreTestInstruction(false);
                                            }}
                                            className="py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 font-black text-white text-xs shadow-md shadow-indigo-600/10 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                        >
                                            Yes, Begin Test <FaPlay className="text-[8px]" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PreCheck Cancel Footer */}
                        {preCheckStep < 3 && (
                            <div className="flex justify-end border-t border-gray-100 pt-6 mt-4">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="px-6 py-2.5 rounded-xl font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all text-xs cursor-pointer"
                                >
                                    Cancel & Exit
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Section Instruction View
    if (isInstruction) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center animate-fade-in">
                <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mb-8 animate-pulse">
                    <FaVolumeUp className="text-4xl text-indigo-600" />
                </div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">
                    Part {sectionOrder.indexOf(sectionName) + 1}: {sectionName.replace(/([A-Z])/g, ' $1').trim()}
                </h2>
                <div className="bg-gray-50 p-8 rounded-2xl max-w-2xl border border-gray-100 shadow-sm">
                    <p className="text-2xl text-gray-700 leading-relaxed font-medium">
                        "{speakingData.instructions ? speakingData.instructions[sectionName] : 'Please follow the instructions.'}"
                    </p>
                </div>
                <p className="mt-8 text-gray-400 text-sm tracking-widest uppercase">
                    Listen to instructions • Starting soon...
                </p>
            </div>
        );
    }

    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return <div className="min-h-screen flex items-center justify-center">Loading Question...</div>;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            {/* Navbar */}
            <div className="bg-white shadow px-6 py-4 flex justify-between items-center">
                <h1 className="font-bold text-gray-800 text-lg">Versant Speaking Test</h1>
                <div className="bg-gray-100 rounded-full px-4 py-1 text-sm font-medium text-gray-600">
                    {sectionName.replace(/([A-Z])/g, ' $1').trim()} • Q{currentQuestionIndex + 1}/{questions.length}
                </div>
            </div>

            {/* Main Area */}
            <main className="flex-1 flex flex-col items-center justify-center p-6 max-w-4xl mx-auto w-full">

                {/* Question Card */}
                <div className="bg-white rounded-3xl shadow-xl w-full p-8 md:p-12 text-center transition-all duration-300">

                    {/* Status Indicator */}
                    {isListening ? (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full font-bold mb-8 animate-pulse">
                            <FaVolumeUp /> Playing Audio...
                        </div>
                    ) : isRecording ? (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full font-bold mb-8 animate-pulse">
                            <FaMicrophone /> Recording...
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-full font-bold mb-8">
                            Waiting...
                        </div>
                    )}

                    {/* Prompt Display */}
                    <div className="mb-10 min-h-[120px] flex items-center justify-center">
                        {sectionName === 'readAloud' ? (
                            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 leading-snug">
                                "{currentQ.text}"
                            </h2>
                        ) : sectionName === 'openQuestions' ? (
                            <h2 className="text-2xl font-medium text-gray-800">
                                {currentQ.question}
                            </h2>
                        ) : (
                            <div className="text-gray-400 italic text-xl flex flex-col items-center gap-2">
                                <FaVolumeUp className="text-3xl" />
                                <span>Listen carefully... (No text displayed)</span>
                            </div>
                        )}
                    </div>

                    {/* Hidden Transcript Area (For Real Test Feel) */}
                    <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-center border-2 border-dashed border-gray-200">
                        {isRecording ? (
                            <p className="text-gray-500 animate-pulse">Recording your response...</p>
                        ) : (
                            <p className="text-gray-400">Audio will play, then recording starts automatically.</p>
                        )}
                    </div>

                    {/* Auto-Flow Controls Indicator / Visualizer */}
                    <div className="flex flex-col items-center gap-4 w-full h-32 justify-end">
                        {isRecording ? (
                            <div className="flex flex-col items-center animate-fade-in">
                                <p className="text-red-500 font-bold mb-2 animate-pulse">🔴 Recording Live...</p>
                                <canvas ref={canvasRef} width="400" height="80" className="w-full max-w-sm bg-gray-50 rounded-lg" />
                            </div>
                        ) : (
                            <div className="w-full max-w-md">
                                {isListening ? (
                                    <div className="flex flex-col items-center">
                                        <FaVolumeUp className="text-4xl text-blue-500 mb-2 animate-bounce" />
                                        <div className="h-1 w-full bg-blue-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 animate-progress"></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-1 w-full bg-gray-200 rounded-full"></div>
                                )}
                            </div>
                        )}

                        <p className="text-sm text-gray-400 font-medium tracking-wide">
                            {isListening ? "Listen Carefully" : isRecording ? "Speak Now" : "Preparing..."}
                        </p>
                    </div>

                    {feedbackMessage && <p className="mt-4 text-red-500 text-sm">{feedbackMessage}</p>}

                    {/* Manual Navigation Controls (Centered Bottom) */}
                    <div className="mt-8 flex justify-center">
                        <button
                            onClick={handleNext}
                            className="flex items-center gap-3 px-8 py-3 bg-gray-900 hover:bg-black text-white rounded-full font-bold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        >
                            Next Question <FaPlay className="text-xs" />
                        </button>
                    </div>

                </div>
            </main>
        </div>
    );
};

export default SpeakingTest;
